import { Auth } from "aws-amplify";

import AppBackService from "./appback";

// Sign in to Tapestry on Zoom's word (TT-1208).
//
// Inside Zoom the embedded browser has no Tapestry session, but Zoom knows the
// user. Its app context — encrypted with our app's client secret, so only the
// backend can read it — is the proof:
//
//   1. getAppContext()                       the proof, relayed unread
//   2. appback /zoom/app-signin              whose account it is
//   3. Auth.signIn(username), no password    Cognito's custom sign-in
//   4. answer the challenge with the context Cognito's trigger checks it again
//
// The result is an ordinary Amplify session. Anything short of that (not
// linked, context refused, a network failure) returns false and the caller
// shows the normal login. The context is a credential until it expires:
// never log it.

// How long the caller waits before showing the login screen. The attempt is
// not cancelled at that point (its network calls cannot be); if it still
// lands, `onSignedIn` says so.
export const ZOOM_SIGN_IN_TIMEOUT_MS = 10000;

const hasSession = async () => {
  try {
    await Auth.currentSession();
    return true;
  } catch (e) {
    return false;
  }
};

const attempt = async (zoomSdk) => {
  if (await hasSession()) {
    return false;
  }
  const { context } = (await zoomSdk.getAppContext()) ?? {};
  if (!context) {
    return false;
  }
  const who = await AppBackService.zoomAppSignin(context);
  if (!who?.username) {
    return false;
  }
  const user = await Auth.signIn(who.username);
  if (user?.challengeName !== "CUSTOM_CHALLENGE") {
    return false;
  }
  // This can outlive its timeout, and the user may have signed in by hand
  // meanwhile. Their session stands; do not replace it.
  if (await hasSession()) {
    return false;
  }
  await Auth.sendCustomChallengeAnswer(user, context);
  return hasSession();
};

// Resolves true when this call created a session within `timeoutMs`. Never
// throws. `onSignedIn` runs whenever the attempt creates a session, in time
// or late, so the app's state always follows the session that exists.
export const signInWithZoom = async (zoomSdk, { onSignedIn, timeoutMs = ZOOM_SIGN_IN_TIMEOUT_MS } = {}) => {
  const run = attempt(zoomSdk)
    .then(async (signedIn) => {
      if (signedIn && onSignedIn) {
        await onSignedIn();
      }
      return signedIn;
    })
    .catch((e) => {
      // The error's name only: an Amplify error can echo what it was sent.
      console.log("Zoom sign-in did not complete:", e?.name ?? "error");
      return false;
    });
  let timer;
  const gaveUp = new Promise((resolve) => {
    timer = setTimeout(() => resolve(false), timeoutMs);
  });
  try {
    return await Promise.race([run, gaveUp]);
  } finally {
    clearTimeout(timer);
  }
};
